import { ColumnMatcherTypes } from "../types/rules/ColumnMatcherTypes";
import { ColumnConfig } from "./config/ColumnConfig";
import { ColumnSection, ColumnSectionData } from "./matchers/ColumnSection";
import { DlfMatcher, DlfMatcherProperties } from "./matchers/DlfMatcher";
import { MatcherFactory } from "./matchers/factory/MatcherFactory";
import { MatcherProperties } from "./matchers/Matcher";
import { RegexFieldMatcher } from "./matchers/RegexFieldMatcher";

type ColumnMatcherData = {
  discriminator: ColumnMatcherTypes;
  // FE only
  showColumn?: boolean;
  columnName: string;
  anchor: boolean;
  anchorReverse: boolean;
  // replace with ColumnSection
  // leftXPercent: number;
  // rightXPercent: number;
  columnSection: ColumnSectionData;
  matcher: DlfMatcherProperties;
} & Omit<MatcherProperties, "discriminator">;

class ColumnMatcher implements ColumnMatcherData {
  discriminator: ColumnMatcherTypes = "C_COLUMN";
  // leftXPercent: number;
  // rightXPercent: number;
  columnSection: ColumnSection;
  anchor: boolean;
  anchorReverse: boolean;
  columnName: string;
  position: number;
  reusable: boolean;
  showColumn: boolean;
  threshold: number;

  matcher: DlfMatcher;

  constructor(data: ColumnMatcherData) {
    this.anchor = data.anchor;
    this.anchorReverse = data.anchorReverse;
    this.columnSection = new ColumnSection(data.columnSection);
    this.showColumn = data.showColumn ?? false;
    this.columnName = data.columnName;
    this.matcher = MatcherFactory.createDlfMatcher(data.matcher);
    // if (this.matcher.discriminator === 'F_REGEX') {
    // (this.matcher as RegexFieldMatcher).regex = '.+'
    // }
    this.position = data.position;
    this.reusable = data.reusable;
    this.threshold = data.threshold;
  }
  isConfigured(): boolean {
    // regex not empty, and X values aren't 0
    return !(this.matcher.isConfigured() && this.columnSection.isConfigured());
  }

  getMatcherId(): string {
    // actively ignore threshold property
    return `${this.discriminator}-[${this.matcher.getMatcherId()}]-[${this.columnSection.getSectionId()}]-[${this.anchor}-${this.anchorReverse}]]`;
  }

  getMatcherIdWithoutRange(): string {
    return `${this.discriminator}-[${this.matcher.getMatcherId()}]-[${this.anchor}-${this.anchorReverse}]]`;
  }

  getDiscriminator(): ColumnMatcherTypes {
    return this.discriminator;
  }
  clear(): void {
    this.columnSection.clear();
    this.matcher.clear();
  }
  getReusable(): boolean {
    return false;
  }
  setReusable(value: boolean): void {}

  static createFromConfig(columnConfig: ColumnConfig): ColumnMatcherData {
    const matcherData: ColumnMatcherData = {
      columnName: columnConfig.fieldName,
      anchor: false,
      anchorReverse: false,
      position: 0,
      reusable: true,
      columnSection: {
        discriminator: "COLUMN",
        leftXPercent: 0,
        rightXPercent: 0,
      },
      discriminator: "C_COLUMN",
      showColumn: false,
      threshold: 0,
      matcher: RegexFieldMatcher.createTest({
        fieldName: columnConfig.fieldName,
        regex: ".+",
      }).toMatcherData(),
    };

    return matcherData;
  }

  toMatcherData(): ColumnMatcherData {
    return {
      discriminator: "C_COLUMN",
      columnName: this.columnName,
      position: this.position,
      reusable: this.reusable,
      anchor: this.anchor,
      anchorReverse: this.anchorReverse,
      columnSection: this.columnSection.toSectionData(),
      showColumn: this.showColumn,
      threshold: this.threshold,
      matcher: this.matcher.toMatcherData(),
    };
  }

  static createTest(rest: Partial<ColumnMatcherData> = {}): ColumnMatcher {
    const data: ColumnMatcherData = {
      columnName: "",
      position: 0,
      reusable: true,
      discriminator: "C_COLUMN",
      anchor: false,
      anchorReverse: false,
      columnSection: {
        discriminator: "COLUMN",
        leftXPercent: 0,
        rightXPercent: 0,
      },
      threshold: 0,
      matcher: RegexFieldMatcher.createTest().toMatcherData(),
    };

    return new ColumnMatcher({
      ...data,
      ...rest,
    });
  }
}

export { ColumnMatcher, ColumnMatcherData };
